export type ChunkState = "unloaded" | "loading" | "decoded" | "active" | "evicting";
export type QualityProfile = "low" | "medium" | "high";
export interface Profile {
  lod0Radius: number;
  lod1Radius: number;
  unloadRadius: number;
  dpr: number;
  cache: number;
  pedestrians: number;
  vehicles: number;
  streetLightShadowCount: number;
  headlightShadowVehicleCount: number;
}
export const PROFILES: Record<QualityProfile, Profile> = {
  low: {
    lod0Radius: 2,
    lod1Radius: 6,
    unloadRadius: 8,
    dpr: 1,
    cache: 128,
    pedestrians: 60,
    vehicles: 40,
    streetLightShadowCount: 0,
    headlightShadowVehicleCount: 0,
  },
  medium: {
    lod0Radius: 3,
    lod1Radius: 8,
    unloadRadius: 10,
    dpr: 1.5,
    cache: 256,
    pedestrians: 120,
    vehicles: 80,
    streetLightShadowCount: 1,
    headlightShadowVehicleCount: 1,
  },
  high: {
    lod0Radius: 4,
    lod1Radius: 10,
    unloadRadius: 12,
    dpr: 2,
    cache: 384,
    pedestrians: 180,
    vehicles: 120,
    streetLightShadowCount: 2,
    headlightShadowVehicleCount: 1,
  },
};
export interface ResidentChunk {
  key: string;
  x: number;
  z: number;
  lod: 0 | 1;
  state: ChunkState;
  priority: number;
  pinned: boolean;
}

export class WorldStreamer {
  readonly residents = new Map<string, ResidentChunk>();
  profile: Profile;
  constructor(profile: QualityProfile = "medium") {
    this.profile = PROFILES[profile];
  }

  reconcile(playerX: number, playerZ: number, routePins: readonly string[] = []): void {
    const centreX = Math.floor(playerX / 128);
    const centreZ = Math.floor(playerZ / 128);
    const wanted = new Map<string, ResidentChunk>();
    for (
      let z = centreZ - this.profile.lod1Radius;
      z <= centreZ + this.profile.lod1Radius;
      z += 1
    ) {
      for (
        let x = centreX - this.profile.lod1Radius;
        x <= centreX + this.profile.lod1Radius;
        x += 1
      ) {
        const distance = Math.hypot(x - centreX, z - centreZ);
        if (distance > this.profile.lod1Radius) continue;
        const lod: 0 | 1 = distance <= this.profile.lod0Radius ? 0 : 1;
        const key = `${x}_${z}`;
        wanted.set(key, {
          key,
          x,
          z,
          lod,
          state: "active",
          priority: lod === 0 ? 3 : 1,
          pinned: routePins.includes(key),
        });
      }
    }
    for (const pin of routePins.slice(0, 4)) {
      const [x = 0, z = 0] = pin.split("_").map(Number);
      wanted.set(pin, { key: pin, x, z, lod: 0, state: "active", priority: 4, pinned: true });
    }
    const ordered = [...wanted.values()].sort(
      (a, b) =>
        b.priority - a.priority ||
        Number(b.pinned) - Number(a.pinned) ||
        Math.hypot(a.x - centreX, a.z - centreZ) - Math.hypot(b.x - centreX, b.z - centreZ),
    );
    this.residents.clear();
    for (const chunk of ordered.slice(0, this.profile.cache)) this.residents.set(chunk.key, chunk);
    if (this.residents.size > this.profile.cache) throw new Error("Chunk residency cap exceeded");
  }

  setProfile(profile: QualityProfile): void {
    this.profile = PROFILES[profile];
  }
  dispose(): void {
    this.residents.clear();
  }
}
